import React, { useState, useEffect, useRef } from 'react';
import { 
  Folder, 
  File, 
  Upload, 
  Plus, 
  MoreVertical, 
  Edit, 
  Trash2, 
  Download, 
  FolderPlus,
  FileText,
  Image,
  FileSpreadsheet,
  FileType,
  ChevronRight,
  ChevronDown,
  RefreshCw,
  Search,
  Filter,
  Grid,
  List,
  Home,
  ArrowLeft,
  Eye,
  X,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Maximize2,
  Minimize2,
  Play,
  CheckCircle,
  AlertCircle,
  PlusCircle,
  Edit3,
  FileX
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import ModernLayout from './ModernLayout';
import './ModernFileManager.css';
import config, { getDefaultHeaders, getCountryHeader } from '../config/api';
import { useCountry } from '../context/CountryContext';

const ModernFileManager = () => {
  const { country } = useCountry();
  const [currentPath, setCurrentPath] = useState('data');
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [searchQuery, setSearchQuery] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [previewFile, setPreviewFile] = useState(null);
  const [previewContent, setPreviewContent] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, item: null });
  const [processingStatus, setProcessingStatus] = useState({
    isProcessing: false,
    progress: 0,
    message: '',
    error: null,
    hasChanges: false,
    processedFiles: 0,
    totalFiles: 0,
    failedFiles: 0,
    skippedFiles: 0,
    processedFilesList: []
  });
  const [showProcessingModal, setShowProcessingModal] = useState(false);
  const [fileChanges, setFileChanges] = useState({});

  const fileInputRef = useRef(null);

  // Load directory contents
  const loadDirectoryContents = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${config.apiUrl}/file-manager/contents?path=${encodeURIComponent(currentPath)}`, {
        headers: getDefaultHeaders(country)
      });
      if (!response.ok) {
        throw new Error('Failed to load directory contents');
      }
      const data = await response.json();
      setFiles(data.files || []);
      setFolders(data.folders || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Process files
  const processFiles = async () => {
    setProcessingStatus({
      isProcessing: true,
      progress: 0,
      message: 'Starting file processing...',
      error: null,
      hasChanges: false,
      processedFiles: 0
    });
    setShowProcessingModal(true);

    try {
      const response = await fetch(`${config.apiUrl}/file-processing/process`, {
        method: 'POST',
        headers: {
          ...getDefaultHeaders(country),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ path: currentPath }),
      });

      if (!response.ok) {
        throw new Error('Failed to process files');
      }

      const data = await response.json();
      console.log('Processing API response:', data);
      console.log('Result structure:', data.result);
      console.log('Batch processing:', data.result?.batch_processing);
      
      // Extract processed files count from nested structure
      const processedFiles = data.result?.batch_processing?.processing_summary?.successful || 0;
      const totalFiles = data.result?.batch_processing?.processing_summary?.total_files || 0;
      const failedFiles = data.result?.batch_processing?.processing_summary?.failed || 0;
      const skippedFiles = data.result?.batch_processing?.processing_summary?.skipped || 0;
      const processedFilesList = data.result?.batch_processing?.results || [];
      
      console.log('Extracted stats:', { processedFiles, totalFiles, failedFiles, skippedFiles });

      setProcessingStatus({
        isProcessing: false,
        progress: 100,
        message: 'Processing completed successfully!',
        error: null,
        hasChanges: data.result?.changes || {},
        processedFiles: processedFiles,
        totalFiles: totalFiles,
        failedFiles: failedFiles,
        skippedFiles: skippedFiles,
        processedFilesList: processedFilesList
      });

      // Reload directory contents to show changes
      await loadDirectoryContents();
    } catch (err) {
      setProcessingStatus({
        isProcessing: false,
        progress: 0,
        message: 'Processing failed',
        error: err.message,
        hasChanges: false,
        processedFiles: 0
      });
    }
  };

  // Handle folder navigation
  const handleFolderClick = (folderName) => {
    const newPath = currentPath === 'data' ? `data/${folderName}` : `${currentPath}/${folderName}`;
    setCurrentPath(newPath);
    setSelectedItems([]);
    setPreviewFile(null);
    setPreviewContent(null);
  };

  // Handle file click
  const handleFileClick = (fileName) => {
    if (selectedItems.includes(fileName)) {
      setSelectedItems(selectedItems.filter(item => item !== fileName));
    } else {
      setSelectedItems([...selectedItems, fileName]);
    }
  };

  // Handle file double click
  const handleFileDoubleClick = (fileName) => {
    handlePreviewClick(fileName);
  };

  // Handle preview click
  const handlePreviewClick = async (fileName) => {
    const file = files.find(f => f.name === fileName);
    if (!file) return;

    setPreviewFile(file);
    setPreviewLoading(true);
    setPreviewError(null);

    try {
      const response = await fetch(`${config.apiUrl}/file-manager/preview?path=${encodeURIComponent(currentPath)}&fileName=${encodeURIComponent(fileName)}`, {
        headers: getDefaultHeaders(country)
      });
      if (!response.ok) {
        throw new Error('Failed to load file preview');
      }

      const contentType = response.headers.get('content-type');
      const fileExtension = fileName.split('.').pop()?.toLowerCase();

      if (contentType?.includes('image/') || ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(fileExtension)) {
        // Handle images
        const blob = await response.blob();
        const imageUrl = URL.createObjectURL(blob);
        setPreviewContent({
          type: 'image',
          url: imageUrl,
          fileName: fileName
        });
      } else if (contentType?.includes('text/') || ['txt', 'md', 'json', 'csv', 'js', 'py', 'html', 'css', 'xml', 'yaml', 'yml'].includes(fileExtension)) {
        // Handle text files
        const text = await response.text();
        setPreviewContent({
          type: 'text',
          content: text,
          fileName: fileName,
          extension: fileExtension
        });
      } else if (fileExtension === 'pdf') {
        // Handle PDFs
        const blob = await response.blob();
        const pdfUrl = URL.createObjectURL(blob);
        setPreviewContent({
          type: 'pdf',
          url: pdfUrl,
          fileName: fileName
        });
      } else {
        // Unsupported file type
        setPreviewContent({
          type: 'unsupported',
          fileName: fileName,
          extension: fileExtension
        });
      }
    } catch (err) {
      setPreviewError(err.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Handle navigation
  const navigateUp = () => {
    if (currentPath === 'data') return;
    const pathParts = currentPath.split('/');
    pathParts.pop();
    setCurrentPath(pathParts.join('/') || 'data');
    setSelectedItems([]);
    setPreviewFile(null);
    setPreviewContent(null);
  };

  const navigateToHome = () => {
    setCurrentPath('data');
    setSelectedItems([]);
    setPreviewFile(null);
    setPreviewContent(null);
  };

  // Handle breadcrumb navigation
  const handleBreadcrumbClick = (index) => {
    const pathParts = currentPath.split('/');
    const newPath = pathParts.slice(0, index + 1).join('/') || 'data';
    setCurrentPath(newPath);
    setSelectedItems([]);
    setPreviewFile(null);
    setPreviewContent(null);
  };

  // Handle file upload
  const handleUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setLoading(true);
    try {
      // Upload files one by one since API only supports single file upload
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('path', currentPath);

        // For FormData, don't set Content-Type - let browser set it with boundary
        const headers = getCountryHeader(country);

        const response = await fetch(`${config.apiUrl}/file-manager/upload`, {
          method: 'POST',
          headers: headers,
          body: formData,
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to upload ${file.name}: ${errorText}`);
        }
      }

      await loadDirectoryContents();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      // Reset file input to allow selecting the same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle folder creation
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;

    setLoading(true);
    try {
      const response = await fetch(`${config.apiUrl}/file-manager/create-folder`, {
        method: 'POST',
        headers: {
          ...getDefaultHeaders(country),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          path: currentPath,
          folderName: newFolderName.trim(),
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create folder');
      }

      setNewFolderName('');
      setShowNewFolderModal(false);
      await loadDirectoryContents();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle file/folder deletion
  const handleDelete = async (name, isFolder) => {
    if (!window.confirm(`Are you sure you want to delete ${isFolder ? 'folder' : 'file'} "${name}"?`)) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${config.apiUrl}/file-manager/delete`, {
        method: 'DELETE',
        headers: {
          ...getDefaultHeaders(country),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          path: currentPath,
          itemName: name,
          isFolder,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to delete item');
      }

      await loadDirectoryContents();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle file download
  const handleDownload = async (fileName) => {
    try {
      const response = await fetch(`${config.apiUrl}/file-manager/download?path=${encodeURIComponent(currentPath)}&file=${encodeURIComponent(fileName)}`, {
        headers: getDefaultHeaders(country)
      });
      if (!response.ok) {
        throw new Error('Failed to download file');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(err.message);
    }
  };

  // Handle context menu
  const handleContextMenu = (e, item) => {
    e.preventDefault();
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      item,
    });
  };

  // Close preview
  const closePreview = () => {
    setPreviewFile(null);
    setPreviewContent(null);
    setPreviewError(null);
    setZoomLevel(1);
    setIsFullScreen(false);
  };

  // Zoom controls
  const handleZoomIn = () => setZoomLevel(prev => Math.min(prev + 0.1, 3));
  const handleZoomOut = () => setZoomLevel(prev => Math.max(prev - 0.1, 0.1));
  const handleResetZoom = () => setZoomLevel(1);

  // Toggle fullscreen
  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
  };

  // Get file icon
  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    
    switch (extension) {
      case 'pdf':
        return <FileText size={16} className="file-icon" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
      case 'svg':
        return <Image size={16} className="file-icon" />;
      case 'xlsx':
      case 'xls':
      case 'csv':
        return <FileSpreadsheet size={16} className="file-icon" />;
      case 'txt':
      case 'md':
      case 'json':
      case 'js':
      case 'py':
      case 'html':
      case 'css':
        return <FileType size={16} className="file-icon" />;
      default:
        return <File size={16} className="file-icon" />;
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get file change status
  const getFileChangeStatus = (item) => {
    console.log('Checking change status for:', item.name, 'isFolder:', item.isFolder);
    console.log('Current path:', currentPath);
    console.log('Current fileChanges:', fileChanges);
    
    // Check if the item itself has changes
    const directChange = fileChanges[item.name];
    if (directChange) {
      console.log('Direct change found:', directChange);
      return directChange;
    }

    // For folders, check if any files within the folder or its subfolders have changes
    if (item.isFolder) {
      // Construct the path relative to data folder (without data/ prefix)
      let relativeFolderPath;
      if (currentPath === 'data') {
        // At root level, just use the folder name
        relativeFolderPath = item.name;
      } else {
        // Remove 'data/' prefix from currentPath if it exists
        const cleanCurrentPath = currentPath.startsWith('data/') ? currentPath.substring(5) : currentPath;
        relativeFolderPath = `${cleanCurrentPath}/${item.name}`;
      }
      console.log('Checking folder path:', relativeFolderPath);
      
      // Check all change types for files in this folder
      const allChangedFiles = [
        ...(fileChanges.new_files || []),
        ...(fileChanges.modified_files || []),
        ...(fileChanges.missing_summaries || [])
      ];
      console.log('All changed files:', allChangedFiles);
      
      // Check if any changed file is in this folder or its subfolders
      for (const changedFile of allChangedFiles) {
        // Normalize paths for comparison
        const normalizedChangedFile = changedFile.replace(/\\/g, '/');
        const normalizedFolderPath = relativeFolderPath.replace(/\\/g, '/');
        
        console.log('Comparing:', normalizedChangedFile, 'with folder:', normalizedFolderPath);
        
        // Check if the changed file is within this folder (including subfolders)
        if (normalizedChangedFile.startsWith(normalizedFolderPath + '/') || 
            normalizedChangedFile === normalizedFolderPath) {
          console.log('Match found!');
          // Determine the type of change for the folder indicator
          if (fileChanges.missing_summaries && fileChanges.missing_summaries.includes(changedFile)) {
            console.log('Returning missing status');
            return 'missing';
          } else if (fileChanges.new_files && fileChanges.new_files.includes(changedFile)) {
            console.log('Returning new status');
            return 'new';
          } else if (fileChanges.modified_files && fileChanges.modified_files.includes(changedFile)) {
            console.log('Returning modified status');
            return 'modified';
          }
        }
      }
    }

    console.log('No change status found');
    return null;
  };

  // Check for file changes
  const checkFileChanges = async () => {
    try {
      const response = await fetch(`${config.apiUrl}/file-processing/check-changes`, {
        headers: getDefaultHeaders(country)
      });
      if (response.ok) {
        const data = await response.json();
        console.log('Change detection API response:', data);
        console.log('Changes object:', data.changes);
        setFileChanges(data.changes || {});
        setProcessingStatus(prev => ({
          ...prev,
          hasChanges: data.hasChanges || false
        }));
      }
    } catch (err) {
      console.error('Failed to check file changes:', err);
    }
  };

  // Filter files and folders based on search query
  const filteredFolders = folders.filter(folder =>
    folder.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const filteredFiles = files.filter(file =>
    file.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Load directory contents on mount and when path changes
  useEffect(() => {
    loadDirectoryContents();
  }, [currentPath]);

  // Check for changes when component mounts and when files change
  useEffect(() => {
    checkFileChanges();
  }, [files, folders]);

  // Left Panel Component
  const leftPanel = (
    <div className="file-manager-left-panel">
      {/* Panel Header */}
      <div className="panel-header">
        <div className="panel-title">File Manager</div>
        <div className="panel-description">Manage your backend files and folder structure</div>
      </div>

      {/* File Processing Section */}
      <div className="config-section">
        <div className="config-label">File Processing</div>
        <button 
          className="process-btn"
          onClick={processFiles}
          disabled={processingStatus.isProcessing}
        >
          <Play size={16} />
          <span>Process Files</span>
        </button>
        {processingStatus.hasChanges && (
          <div className="changes-indicator">
            <AlertCircle size={14} />
            <span>Changes detected</span>
          </div>
        )}
      </div>

      {/* Search Section */}
      <div className="config-section">
        <div className="config-label">Search Files</div>
        <div className="search-container">
          <Search size={16} className="search-icon" />
          <input
            type="text"
            placeholder="Search files and folders..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="config-section">
        <div className="config-label">Actions</div>
        <div className="action-buttons">
          <button 
            className="action-btn"
            onClick={() => setShowNewFolderModal(true)}
            title="New Folder"
          >
            <FolderPlus size={16} />
          </button>
          <button 
            className="action-btn"
            onClick={() => fileInputRef.current?.click()}
            title="Upload Files"
          >
            <Upload size={16} />
          </button>
          <button 
            className="action-btn"
            onClick={loadDirectoryContents}
            title="Refresh"
          >
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* Navigation Section */}
      <div className="config-section">
        <div className="config-label">Navigation</div>
        <div className="navigation-group">
          <button 
            className="nav-btn"
            onClick={navigateUp}
            disabled={currentPath === 'data'}
            title="Go Up"
          >
            <ArrowLeft size={16} />
          </button>
          <button 
            className="nav-btn"
            onClick={navigateToHome}
            title="Home"
          >
            <Home size={16} />
          </button>
        </div>
        
        {/* Breadcrumb */}
        <div className="breadcrumb">
          {currentPath.split('/').map((part, index) => (
            <React.Fragment key={index}>
              {index > 0 && <ChevronRight size={14} className="breadcrumb-separator" />}
              <button
                className="breadcrumb-item"
                onClick={() => handleBreadcrumbClick(index)}
              >
                {part}
              </button>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* File List */}
      <div className="config-section">
        <div className="config-label">Files & Folders</div>
        <div className="file-list-container">
          {loading ? (
            <div className="loading-state">
              <RefreshCw className="loading-icon spinning" size={20} />
              <p>Loading...</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <p>{error}</p>
              <button className="retry-btn" onClick={loadDirectoryContents}>
                Try Again
              </button>
            </div>
          ) : (
            <div className={`file-list ${viewMode}`}>
              {/* Folders */}
              {filteredFolders.map((folder) => {
                const changeStatus = getFileChangeStatus({ ...folder, isFolder: true });
                return (
                  <div
                    key={folder.name}
                    className={`file-item folder ${selectedItems.includes(folder.name) ? 'selected' : ''} ${changeStatus ? `has-changes ${changeStatus}` : ''}`}
                    onClick={() => handleFolderClick(folder.name)}
                    onContextMenu={(e) => handleContextMenu(e, { ...folder, isFolder: true })}
                  >
                    <div className="file-icon-container">
                      <Folder className="file-icon" size={16} />
                    </div>
                    <div className="file-info">
                      <div className="file-name">
                        {folder.name}
                        {changeStatus && (
                          <AlertCircle 
                            size={12} 
                            className={`change-alert-icon ${changeStatus}`}
                          />
                        )}
                      </div>
                      <div className="file-meta">
                        {formatDate(folder.modified)}
                      </div>
                      {changeStatus && (
                        <span className={`change-badge ${changeStatus}`}>
                          {changeStatus === 'new' && 'New'}
                          {changeStatus === 'modified' && 'Modified'}
                          {changeStatus === 'missing-summary' && 'Needs Summary'}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Files */}
              {filteredFiles.map((file) => {
                const changeStatus = getFileChangeStatus({ ...file, isFolder: false });
                return (
                  <div
                    key={file.name}
                    className={`file-item file ${selectedItems.includes(file.name) ? 'selected' : ''} ${changeStatus ? `has-changes ${changeStatus}` : ''}`}
                    onClick={() => handleFileClick(file.name)}
                    onDoubleClick={() => handleFileDoubleClick(file.name)}
                    onContextMenu={(e) => handleContextMenu(e, { ...file, isFolder: false })}
                  >
                    <div className="file-icon-container">
                      {getFileIcon(file.name)}
                    </div>
                    <div className="file-info">
                      <div className="file-name">
                        {file.name}
                        {changeStatus && (
                          <AlertCircle 
                            size={12} 
                            className={`change-alert-icon ${changeStatus}`}
                          />
                        )}
                      </div>
                      <div className="file-meta">
                        {formatFileSize(file.size)} • {formatDate(file.modified)}
                      </div>
                      {changeStatus && (
                        <span className={`change-badge ${changeStatus}`}>
                          {changeStatus === 'new' && 'New'}
                          {changeStatus === 'modified' && 'Modified'}
                          {changeStatus === 'missing-summary' && 'Needs Summary'}
                        </span>
                      )}
                    </div>
                    <div className="file-actions">
                      <button
                        className="preview-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePreviewClick(file.name);
                        }}
                        title="Preview file"
                      >
                        <Eye size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}

              {/* Empty State */}
              {filteredFolders.length === 0 && filteredFiles.length === 0 && (
                <div className="empty-state">
                  <Folder size={32} className="empty-icon" />
                  <h3>No files or folders</h3>
                  <p>This directory is empty. Upload files or create folders to get started.</p>
                  <div className="empty-actions">
                    <button 
                      className="action-btn primary"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload size={14} />
                      Upload Files
                    </button>
                    <button 
                      className="action-btn"
                      onClick={() => setShowNewFolderModal(true)}
                    >
                      <FolderPlus size={14} />
                      New Folder
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Center Panel Component
  const centerPanel = (
    <div className="file-manager-center-panel">
      {/* Preview Section Header */}
      <div className="preview-section-header">
        <h2 className="preview-section-title">File Preview</h2>
      </div>
      
      {previewFile ? (
        <div className="preview-container">
          {/* Preview Header */}
          <div className="preview-header">
            <div className="preview-title">
              <h3>{previewFile.name}</h3>
              <span className="preview-size">{formatFileSize(previewFile.size)}</span>
            </div>
            <div className="preview-controls">
              {previewContent && previewContent.type === 'image' && (
                <>
                  <button className="control-btn" onClick={handleZoomOut} title="Zoom Out">
                    <ZoomOut size={14} />
                  </button>
                  <span className="zoom-level">{Math.round(zoomLevel * 100)}%</span>
                  <button className="control-btn" onClick={handleZoomIn} title="Zoom In">
                    <ZoomIn size={14} />
                  </button>
                  <button className="control-btn" onClick={handleResetZoom} title="Reset Zoom">
                    <RotateCcw size={14} />
                  </button>
                </>
              )}
              <button className="control-btn" onClick={toggleFullScreen} title={isFullScreen ? "Exit Fullscreen" : "Fullscreen"}>
                {isFullScreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
              </button>
              <button className="control-btn" onClick={closePreview} title="Close Preview">
                <X size={14} />
              </button>
            </div>
          </div>

          {/* Preview Content */}
          <div className="preview-content">
            {previewLoading ? (
              <div className="preview-loading">
                <RefreshCw className="loading-icon spinning" size={24} />
                <p>Loading preview...</p>
              </div>
            ) : previewError ? (
              <div className="preview-error">
                <p>{previewError}</p>
                <button className="retry-btn" onClick={() => handlePreviewClick(previewFile.name)}>
                  Try Again
                </button>
              </div>
            ) : previewContent ? (
              <div className="preview-wrapper">
                {previewContent.type === 'image' && (
                  <div className="image-preview">
                    <img 
                      src={previewContent.url} 
                      alt={previewContent.fileName}
                      style={{ 
                        maxWidth: '100%', 
                        height: 'auto',
                        transform: `scale(${zoomLevel})`,
                        transformOrigin: 'top left'
                      }}
                    />
                  </div>
                )}

                {previewContent.type === 'text' && (
                  <div className="text-preview">
                    <pre className={`text-content ${previewContent.extension}`}>
                      {previewContent.content}
                    </pre>
                  </div>
                )}

                {previewContent.type === 'pdf' && (
                  <div className="pdf-preview">
                    <iframe
                      src={previewContent.url}
                      width="100%"
                      height="100%"
                      title={previewContent.fileName}
                    />
                  </div>
                )}

                {previewContent.type === 'markdown' && (
                  <div className="markdown-preview">
                    <ReactMarkdown>{previewContent.content}</ReactMarkdown>
                  </div>
                )}

                {previewContent.type === 'unsupported' && (
                  <div className="unsupported-preview">
                    <File size={48} className="unsupported-icon" />
                    <h3>Preview not available</h3>
                    <p>This file type ({previewContent.extension}) cannot be previewed.</p>
                    <button 
                      className="action-btn primary"
                      onClick={() => handleDownload(previewFile.name)}
                    >
                      <Download size={14} />
                      Download File
                    </button>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="preview-placeholder">
          <Eye size={48} className="placeholder-icon" />
          <h3>No file selected</h3>
          <p>Double-click a file or click the eye icon to preview it here.</p>
        </div>
      )}
    </div>
  );

  return (
    <ModernLayout 
      leftPanel={leftPanel}
      showTwoPanel={true}
    >
      {centerPanel}
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleUpload}
        style={{ display: 'none' }}
      />

      {/* New Folder Modal */}
      {showNewFolderModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Create New Folder</h3>
              <button
                className="modal-close"
                onClick={() => setShowNewFolderModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <input
                type="text"
                placeholder="Folder name"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                className="modal-input"
                autoFocus
                onKeyPress={(e) => e.key === 'Enter' && handleCreateFolder()}
              />
            </div>
            <div className="modal-footer">
              <button
                className="btn secondary"
                onClick={() => setShowNewFolderModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn primary"
                onClick={handleCreateFolder}
                disabled={!newFolderName.trim()}
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Context Menu */}
      {contextMenu.visible && (
        <div
          className="context-menu"
          style={{
            position: 'fixed',
            left: contextMenu.x,
            top: contextMenu.y,
            zIndex: 1000
          }}
          onClick={() => setContextMenu({ ...contextMenu, visible: false })}
        >
          <button
            className="context-menu-item"
            onClick={() => handleDelete(contextMenu.item.name, contextMenu.item.isFolder)}
          >
            <Trash2 size={14} />
            Delete
          </button>
        </div>
      )}

      {/* Processing Status Modal */}
      {showProcessingModal && (
        <div className="modal-overlay">
          <div className="modal-content processing-modal">
            <div className="modal-header">
              <h3>File Processing Status</h3>
              <button
                className="modal-close"
                onClick={() => setShowProcessingModal(false)}
                disabled={processingStatus.isProcessing}
              >
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="status-content">
                <div className="status-icon">
                  {processingStatus.isProcessing ? (
                    <RefreshCw className="spinning" size={32} />
                  ) : processingStatus.error ? (
                    <AlertCircle size={32} className="error" />
                  ) : (
                    <CheckCircle size={32} className="success" />
                  )}
                </div>
                <div className="status-message">
                  {processingStatus.message || 'Ready to process files'}
                </div>
                {processingStatus.isProcessing && (
                  <div className="progress-bar">
                    <div 
                      className="progress-fill"
                      style={{ width: `${processingStatus.progress}%` }}
                    />
                  </div>
                )}
                {processingStatus.error && (
                  <div className="error-details">
                    <p>{processingStatus.error}</p>
                  </div>
                )}
                {!processingStatus.isProcessing && !processingStatus.error && (
                  <div className="success-details">
                    <p>Processing completed successfully!</p>
                    <div className="processing-stats">
                      <p>Total files: {processingStatus.totalFiles || 0}</p>
                      <p>Successfully processed: {processingStatus.processedFiles || 0}</p>
                      <p>Failed: {processingStatus.failedFiles || 0}</p>
                      <p>Skipped: {processingStatus.skippedFiles || 0}</p>
                    </div>
                    {processingStatus.processedFilesList && processingStatus.processedFilesList.length > 0 && (
                      <div className="processed-files-list">
                        <h4>Processed Files:</h4>
                        <div className="files-list">
                          {processingStatus.processedFilesList.map((file, index) => (
                            <div key={index} className={`file-item ${file.status}`}>
                              <span className="file-name">{file.file_name || file.relative_path}</span>
                              <span className={`status-badge ${file.status}`}>
                                {file.status === 'success' ? '✓' : file.status === 'failed' ? '✗' : '○'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              {processingStatus.isProcessing ? (
                <button
                  className="btn secondary"
                  onClick={() => setShowProcessingModal(false)}
                >
                  Close
                </button>
              ) : (
                <button
                  className="btn primary"
                  onClick={() => setShowProcessingModal(false)}
                >
                  Close
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </ModernLayout>
  );
};

export default ModernFileManager;